import React from "react";
import { Helmet } from "react-helmet-async";
import ProductSchema from "./ProductSchema";
import { Link, useNavigate } from "react-router-dom";
import CosmicBackground from "./CosmicBackground";
// Reuse the styles from the (already live, hydration-safe) /ai-astrologer page so
// every SEO landing page looks consistent.
import styles from "../pages/AiAstrologerPage.module.css";

export type LandingSection = {
  h2: string;
  body?: string[]; // paragraphs
  bullets?: string[]; // <ul>
  steps?: string[]; // <ol>
  /** Sub-sections. Competitors that outrank us on the tool queries do it with
      depth, not with a better tool: an H3 per sub-topic is what lets one page
      answer "what is bhakoot dosha" and "can a manglik marry a non-manglik"
      without spawning a thin page for each. */
  subs?: { h3: string; body: string[] }[];
  /** A real data table. Answer engines and featured snippets lift tables far
      more readily than the same facts written as prose. */
  table?: { head: string[]; rows: string[][]; caption?: string };
  /** Banded results (a gun milan score, say) as cards rather than paragraphs,
      because the reader is looking up ONE band, not reading all four. */
  cards?: { label: string; title: string; body: string; tone?: "bad" | "ok" | "good" | "best" }[];
  /** Related internal links, rendered as a chip row. These are in-content
      links, which carry far more weight than the sitewide nav and footer. */
  chips?: { to: string; label: string }[];
};

export type LandingFaq = { q: string; a: string };

export type LandingConfig = {
  url: string; // canonical, e.g. https://astroaura.ai/free-kundli
  title: string;
  description: string;
  eyebrow: string;
  h1Pre: string;
  h1Accent: string;
  h1Post?: string;
  sub: string;
  primaryCta: string;
  secondaryCta?: string;
  sections: LandingSection[];
  faqs: LandingFaq[];
  schemaName: string;
  schemaDescription: string;
  finalHeading: string;
  finalText: string;
  /** ISO country codes this page's service covers. Defaults to the full
      footprint. Narrow it only for a page that genuinely is single-market. */
  areaServed?: string[];
  /** Currency for the free-tier Offer. Cosmetic on a zero-price offer, but a
      page written for the Gulf should not quote INR. */
  priceCurrency?: string;
  /** Extra internal links in the footer strip, for geo pages that should feed
      the hub pages and be fed by them. */
  relatedLinks?: { to: string; label: string }[];
  /** YMYL disclaimer, rendered under the final CTA. Set it on pages that touch
      marriage, health, money or fear (dosha pages). Pages without one render
      exactly as before. */
  disclaimer?: string;
  /** Page-specific share card, e.g. "/og-kundli-matching.png". Falls back to
      the generic one, so pages without their own are unchanged. */
  ogImage?: string;
  /** Opt in to Article schema by naming the section it belongs to. Only set it
      on a page that genuinely carries article-length prose. */
  articleSection?: string;
  /** Wide two-column reading layout. For long reference pages that carry a
      table and card grids; the default 880px column strands them in whitespace. */
  wide?: boolean;
  /** Drop the hero buttons. On a page whose tool is RIGHT THERE under the
      hero, a "try it" button is a scroll to something already on screen, and it
      pushes the indexable copy further down. */
  hideHeroCtas?: boolean;
  /** The opening block of real copy, directly under the H1. This is the text
      Google reads first and the text a visitor skims first, so it carries the
      head terms in ordinary sentences rather than a slogan. */
  lede?: string[];
  /** Short factual pills beside the lede. Not CTAs: they state what the tool
      does, which is also what the query is asking. */
  heroFacts?: string[];
  /** BCP-47 language of the page copy, e.g. "hi". Defaults to "en". Sets the
      lang attribute on the page container and the schema inLanguage, so a
      Devanagari page is not declared English to crawlers or screen readers.
      It also localises the component's own chrome (FAQ heading, final CTA,
      explore links): a Hindi page was rendering "Frequently asked questions"
      and "Start free" mid-Devanagari, which read as broken. */
  lang?: string;
  /** Quick-fact tiles under the hero: the "राशि एक नज़र में" box every
      ranking rashifal page carries (ruler, element, lucky colour, number,
      day). Featured snippets lift these label:value pairs directly. */
  profile?: { label: string; value: string }[];
};

/* The component's own strings, per language. Only the chrome: all real copy
   comes from the config. */
const UI = {
  en: {
    faqHeading: "Frequently asked questions",
    startFree: "Start free",
    exploreLead: "Explore ",
    exploreTail: ", or go ",
    home: "home",
    baseLinks: [
      { to: "/ai-astrologer", label: "AI astrologer" },
      { to: "/free-kundli", label: "free kundli" },
      { to: "/kundli-matching", label: "kundli matching" },
      { to: "/daily-horoscope", label: "daily horoscope" },
    ],
  },
  hi: {
    faqHeading: "अक्सर पूछे जाने वाले सवाल",
    startFree: "मुफ़्त शुरू कीजिए",
    exploreLead: "और देखिए: ",
    exploreTail: ", या ",
    home: "होम पर जाइए",
    baseLinks: [
      { to: "/ai-jyotish", label: "AI ज्योतिष चैट" },
      { to: "/free-kundli", label: "मुफ़्त कुंडली" },
      { to: "/kundli-matching", label: "कुंडली मिलान" },
      { to: "/daily-horoscope", label: "आज का राशिफल" },
    ],
  },
};

// Every market the product actually serves. This used to be the string "IN" on
// all four landing pages, which told Google the service was India-only, the
// opposite of what a globally available app wants, and a plausible part of why
// nothing surfaced on google.ae or google.com.
const DEFAULT_AREA_SERVED = ["IN", "AE", "US", "GB", "CA", "AU", "SG"];

export default function SeoLandingPage({
  config,
  tool,
  afterIntro,
}: {
  config: LandingConfig;
  /** Optional interactive widget (a calculator) rendered directly under the
      hero, above the prose. Pages without one are unchanged. */
  tool?: React.ReactNode;
  /** Optional block under the opening prose and fact tiles (the tarot deck on
      /usa). Unlike `tool`, it leaves the hero CTAs alone, so they still go to
      the chat. */
  afterIntro?: React.ReactNode;
}) {
  const navigate = useNavigate();
  const goToChat = () => navigate("/chat");
  const ui = config.lang === "hi" ? UI.hi : UI.en;

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: config.schemaName,
    serviceType: "AI Vedic astrology",
    url: config.url,
    areaServed: (config.areaServed ?? DEFAULT_AREA_SERVED).map((c) => ({
      "@type": "Country",
      identifier: c,
    })),
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: "https://astroaura.ai/chat",
      availableLanguage: ["en", "hi"],
    },
    provider: {
      "@type": "Organization",
      name: "Aura AI",
      url: "https://astroaura.ai/",
    },
    description: config.schemaDescription,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: config.priceCurrency ?? "INR",
    },
  };

  /* HowTo and FAQPage JSON-LD were removed 2026-10-23 (audit schema items
     2-3): Google retired HowTo rich results in Sept 2023 and FAQ rich
     results for all sites on 7 May 2026, so both blocks were dead weight.
     The visible step lists and FAQs stay, they are what AI engines quote. */

  /* A share card per page. One generic image for every URL is what makes a
     WhatsApp forward or an Instagram ad look like a stock link; a card that
     names the page is the single cheapest lift to forward rate and CTR. */
  const ogImage = config.ogImage
    ? `https://astroaura.ai${config.ogImage}`
    : "https://astroaura.ai/og-image.png";

  const crumbLabel = `${config.h1Pre} ${config.h1Accent}${config.h1Post ?? ""}`
    .replace(/\s+/g, " ")
    .trim();

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://astroaura.ai/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: crumbLabel,
        item: config.url,
      },
    ],
  };

  // Article, opt-in per page. A tool page with 3,000 words of genuine
  // explanation is an article as well as a tool, and it is the type Google
  // reads for author/publisher/freshness signals. Emitted only where the page
  // really carries that much prose, so the thin pages are not overclaiming.
  const articleJsonLd = config.articleSection
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: config.title.split("|")[0].trim().slice(0, 110),
        description: config.schemaDescription,
        articleSection: config.articleSection,
        inLanguage: config.lang ?? "en",
        isAccessibleForFree: true,
        mainEntityOfPage: { "@type": "WebPage", "@id": config.url },
        image: ogImage,
        author: {
          "@type": "Organization",
          name: "Aura AI",
          url: "https://astroaura.ai/",
        },
        publisher: {
          "@type": "Organization",
          name: "Aura AI",
          url: "https://astroaura.ai/",
          logo: {
            "@type": "ImageObject",
            url: "https://astroaura.ai/logo512.png",
          },
        },
      }
    : null;

  // Bake the document language: react-snap captures documentElement.lang,
  // so Hindi pages ship <html lang="hi"> (audit technical item 4). Reset on
  // unmount so SPA navigation back to an English page drops the tag.
  React.useEffect(() => {
    document.documentElement.lang = config.lang ?? "en";
    return () => {
      document.documentElement.lang = "en";
    };
  }, [config.lang]);

  return (
    <div className={styles.page} lang={config.lang}>
      <ProductSchema />
      <Helmet>
        <title>{config.title}</title>
        <meta name="description" content={config.description} />
        <link rel="canonical" href={config.url} />
        {/* hreflang is NOT set here, IndexPolicy emits the en + x-default pair
            for every indexable route in one place. Setting it here too gave
            these pages duplicate alternate links. */}
        {/* og:type and twitter:card are NOT set here: public/index.html
            already carries them with the same values, and Helmet does not
            remove a static tag it did not render, so setting them again
            shipped two of each. Only the page-SPECIFIC tags belong here. */}
        <meta property="og:locale" content={config.lang === "hi" ? "hi_IN" : "en"} />
        <meta property="og:title" content={config.title} />
        <meta property="og:description" content={config.description} />
        <meta property="og:url" content={config.url} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:alt" content={config.title} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:title" content={config.title} />
        <meta name="twitter:description" content={config.description} />
        <script type="application/ld+json">
          {JSON.stringify(serviceJsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
        {articleJsonLd ? (
          <script type="application/ld+json">
            {JSON.stringify(articleJsonLd)}
          </script>
        ) : null}
      </Helmet>

      <CosmicBackground />

      <main
        className={`${styles.wrap}${config.wide ? ` ${styles.wrapWide}` : ""}`}
      >
        <header className={styles.hero}>
          {/* Optional: a page can open on the H1 rather than a label. */}
          {config.eyebrow ? (
            <p className={styles.eyebrow}>{config.eyebrow}</p>
          ) : null}
          <h1 className={styles.h1}>
            {config.h1Pre} <span className={styles.accent}>{config.h1Accent}</span>
            {config.h1Post ? config.h1Post : null}
          </h1>
          {config.sub ? <p className={styles.sub}>{config.sub}</p> : null}
          {config.heroFacts ? (
            <ul className={styles.factRow}>
              {config.heroFacts.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          ) : null}
          {config.hideHeroCtas ? null : (
          <div className={styles.ctaRow}>
            {/* When the page carries a calculator, the hero CTA is a real
                anchor to it rather than a JS scroll. A previous attempt used
                scrollIntoView/window.scrollTo from an onClick and did nothing
                at all - App.tsx runs `window.scrollTo(0,0)` on every pathname
                change, and a hash link sidesteps that entirely because the
                browser owns the scroll and the pathname never changes.
                Pages with no tool keep the old behaviour. */}
            {tool ? (
              <a className={styles.ctaPrimary} href="#tool">
                {config.primaryCta}
              </a>
            ) : (
              <button className={styles.ctaPrimary} onClick={goToChat}>
                {config.primaryCta}
              </button>
            )}
            {config.secondaryCta ? (
              <button className={styles.ctaGhost} onClick={goToChat}>
                {config.secondaryCta}
              </button>
            ) : null}
          </div>
          )}
        </header>

        {/* The opening prose block. Sits BETWEEN the hero and the tool so the
            page leads with words, which is what the query is about, while the
            tool stays above the fold on a laptop. */}
        {config.lede ? (
          <section className={styles.ledePanel}>
            {config.lede.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </section>
        ) : null}

        {/* Quick-fact tiles. Every page that ranks for "<sign> rashifal"
            opens with this box; it is also the block snippets lift. */}
        {config.profile ? (
          <section className={styles.profileGrid}>
            {config.profile.map((f) => (
              <div key={f.label} className={styles.profileTile}>
                <span className={styles.profileLabel}>{f.label}</span>
                <span className={styles.profileValue}>{f.value}</span>
              </div>
            ))}
          </section>
        ) : null}

        {afterIntro}

        {tool ? (
          <div className={styles.toolSlot} id="tool">
            {tool}
          </div>
        ) : null}

        {/* One column of equal-width cards. See .sectionGrid: a two-column
            version leaves a hole wherever a full-width section follows a
            half-width one, and these sections are far too uneven to order
            around it. The width is filled inside each card instead. */}
        <div className={config.wide ? styles.sectionGrid : undefined}>
        {config.sections.map((s) => (
          <section className={styles.section} key={s.h2}>
            <h2 className={styles.h2}>{s.h2}</h2>
            {s.body
              ? s.body.map((p, i) => <p key={i}>{p}</p>)
              : null}
            {s.steps ? (
              <ol className={styles.steps}>
                {s.steps.map((li, i) => (
                  <li key={i}>{li}</li>
                ))}
              </ol>
            ) : null}
            {s.bullets ? (
              <ul className={styles.asks}>
                {s.bullets.map((li, i) => (
                  <li key={i}>{li}</li>
                ))}
              </ul>
            ) : null}
            {/* Its own scroll container, so a wide table never makes the PAGE
                scroll sideways on a phone. */}
            {s.table ? (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  {s.table.caption ? <caption>{s.table.caption}</caption> : null}
                  <thead>
                    <tr>
                      {s.table.head.map((h) => (
                        <th key={h} scope="col">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {s.table.rows.map((r, i) => (
                      <tr key={i}>
                        {r.map((c, j) =>
                          j === 0 ? (
                            <th key={j} scope="row">
                              {c}
                            </th>
                          ) : (
                            <td key={j}>{c}</td>
                          ),
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            {s.cards ? (
              <div className={styles.bandGrid}>
                {s.cards.map((c) => (
                  <div
                    key={c.label}
                    className={`${styles.band} ${styles[`band_${c.tone ?? "ok"}`]}`}
                  >
                    <span className={styles.bandLabel}>{c.label}</span>
                    <h3 className={styles.bandTitle}>{c.title}</h3>
                    <p>{c.body}</p>
                  </div>
                ))}
              </div>
            ) : null}
            {s.subs
              ? s.subs.map((sub) => (
                  <div className={styles.subsec} key={sub.h3}>
                    <h3 className={styles.h3}>{sub.h3}</h3>
                    {sub.body.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                ))
              : null}
            {s.chips ? (
              <div className={styles.chipRow}>
                {s.chips.map((c) => (
                  <Link key={c.to} to={c.to} className={styles.chip}>
                    {c.label}
                  </Link>
                ))}
              </div>
            ) : null}
          </section>
        ))}
        </div>

        <section className={`${styles.section} ${styles.faqSection}`}>
          <h2 className={styles.h2}>{ui.faqHeading}</h2>
          <div className={`${styles.faq} ${styles.faqGrid}`}>
            {config.faqs.map((f) => (
              <details key={f.q} className={styles.faqItem}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className={styles.finalCta}>
          <h2 className={styles.h2}>{config.finalHeading}</h2>
          <p>{config.finalText}</p>
          <button className={styles.ctaPrimary} onClick={goToChat}>
            {ui.startFree}
          </button>
          <p className={styles.links}>
            {ui.exploreLead}
            {ui.baseLinks.map((l, i) => (
              <span key={l.to}>
                {i > 0 ? ", " : null}
                <Link to={l.to}>{l.label}</Link>
              </span>
            ))}
            {(config.relatedLinks ?? []).map((l) => (
              <span key={l.to}>
                , <Link to={l.to}>{l.label}</Link>
              </span>
            ))}
            {ui.exploreTail}
            <Link to="/">{ui.home}</Link>.
          </p>
        </section>

        {config.disclaimer && (
          <section className={styles.finalCta}>
            <p className={styles.links}>{config.disclaimer}</p>
          </section>
        )}
      </main>
    </div>
  );
}
